import React, { useState, useMemo, useEffect } from 'react';
import Card from "components/card";
import ReactApexChart from 'react-apexcharts';
import { SelectField, RadioGroupField, Radio } from "@aws-amplify/ui-react";
import { useDealData, sortDealPeriods } from "contexts/DealContext";
import { usePortfolioData, sortPortfolioPeriods } from "contexts/PortfolioContext";
import { useViewMode } from "contexts/ViewModeContext";

const isTrailingPeriod = (p) => /^T(12|6|3|1)\b/.test(p);
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const parseMonthPeriod = (label) => {
    const m = label && label.match(/^(\w+)\s+(\d{4})$/);
    if (!m) return null;
    const idx = MONTH_NAMES.indexOf(m[1]);
    if (idx < 0) return null;
    return { year: parseInt(m[2], 10), month: idx };
};

const formatPeriodLabel = (period) => {
    // if (period.toLowerCase().includes('projection')) {
    //     return 'Projection';
    // }

    const match = period.match(/(T\d+)\s+(\w+)\s+(\d{4})/);
    if (match) {
        const [_, trailingPeriod, month, year] = match;
        return `${trailingPeriod} ${month.slice(0, 3)} ${year}`;
    }

    return period;
};

const TrendChart = ({ type }) => {
    const { viewMode } = useViewMode();
    const {
        currentDeal: dealEntity,
        currentFacility: dealFacility,
        selectedPeriod: dealSelectedPeriod,
    } = useDealData();
    const {
        currentPortfolio: portfolioEntity,
        currentPortfolioFacility,
        selectedPeriod: portfolioSelectedPeriod,
    } = usePortfolioData();

    // Pick the current entity based on view mode. Keeps the rest of the chart
    // code below unchanged — it operates on `currentDeal` / `currentFacility`
    // regardless of source.
    const currentDeal = viewMode === 'portfolio' ? portfolioEntity : dealEntity;
    const currentFacility = viewMode === 'portfolio' ? currentPortfolioFacility : dealFacility;
    const selectedPeriod = viewMode === 'portfolio' ? portfolioSelectedPeriod : dealSelectedPeriod;
    const sortPeriods = viewMode === 'portfolio' ? sortPortfolioPeriods : sortDealPeriods;

    // Trend type state (Annual or PPD)
    const [trendType, setTrendType] = useState('Annual');

    // Period view state — trailing periods (T12/T6/T3/T1) are annualized while
    // monthly periods are raw dollars, so plotting them on the same chart is
    // misleading. This toggle limits the chart to one or the other.
    const [periodView, setPeriodView] = useState('trailing');

    // Which period keys the chart should plot. Portfolio has both trailing and
    // monthly periods and mixing them is misleading (trailing = annualized,
    // monthly = raw), so the toggle filters to one bucket. Deals only have
    // trailing periods, so we plot everything.
    const chartPeriods = useMemo(() => {
        const consolidated = currentDeal?.facilities?.["Consolidated"]?.periods || {};
        const allKeys = Object.keys(consolidated);
        if (viewMode !== 'portfolio') {
            return sortPeriods(allKeys);
        }
        const filtered = allKeys.filter(p =>
            periodView === 'trailing' ? isTrailingPeriod(p) : !isTrailingPeriod(p)
        );
        if (periodView === 'monthly') {
            return filtered.slice().sort((a, b) => {
                const pa = parseMonthPeriod(a);
                const pb = parseMonthPeriod(b);
                if (!pa || !pb) return 0;
                return pa.year !== pb.year ? pa.year - pb.year : pa.month - pb.month;
            });
        }
        return sortPeriods(filtered);
    }, [currentDeal, periodView, sortPeriods, viewMode]);

    // Get metrics from current facility and period
    const items = useMemo(() => {
        const categoryData = currentFacility?.periods?.[selectedPeriod]?.Categories?.[type === 'EBITDAR' ? 'Expenses' : type] || [];
        return categoryData?.map(item => item.Type)?.filter(item => {
            if (type === 'EBITDAR') {
                return (item.includes('EBITDAR') || item.includes('EBITDA')) && !item.includes('Check');
            }
            return !item.includes('EBITDAR') && !item.includes('EBITDA') && item !== 'Total' && !item.includes('Check');
        });
    }, [currentFacility, selectedPeriod, type]);

    // Selected metric state with default
    const [selectedMetric, setSelectedMetric] = useState(() => {
        const defaultMetric = type === 'Revenue' ? 'Adjusted Revenue'
            : type === 'Expenses' ? 'Adjusted Expenses'
                : type === 'EBITDAR' ? 'Adjusted EBITDAR'
                    : '';
        return defaultMetric;
    });

    // Selected facilities state with current facility as default
    const [selectedFacilities, setSelectedFacilities] = useState(() => {
        const facilityName = currentFacility?.details?.Facilities;
        return facilityName ? [facilityName] : [];
    });

    // Available facilities
    const facilities = useMemo(() => {
        return currentDeal?.facilities ?
            Object.keys(currentDeal.facilities).filter(name => name.toLowerCase() !== 'consolidated') : [];
    }, [currentDeal]);

    // Update selections when deal/facility changes
    useEffect(() => {
        const defaultMetric = type === 'Revenue' ? 'Adjusted Revenue'
            : type === 'Expenses' ? 'Adjusted Expenses'
                : type === 'EBITDAR' ? 'Adjusted EBITDAR'
                    : '';
        setSelectedMetric(defaultMetric);

        const facilityName = currentFacility?.details?.Facilities;
        if (facilityName) {
            setSelectedFacilities([facilityName]);
        }
    }, [currentDeal, currentFacility, type]);

    // Prepare chart data
    const chartData = useMemo(() => {
        if (!selectedMetric || !currentDeal?.facilities) return [];

        const colors = ['#405F78', '#755263', '#f0b41c', '#2397EB', '#BFCAD2', '#40464B', '#445CAB', '#968148'];

        let chartSeries = [];

        // Add current facility or group data
        if (currentFacility) {
            const facilityName = currentFacility.details?.Facilities;

            if (currentFacility.details?.isGroup) {
                // Add group data - use currentFacility directly as it has the consolidated group data
                const groupData = {
                    name: facilityName,
                    data: chartPeriods.map(period => {
                        const periodData = currentFacility.periods[period];
                        const categoryData = periodData?.Categories?.[type === 'EBITDAR' ? 'Expenses' : type] || [];
                        const metricData = categoryData.find(item => item.Type === selectedMetric);
                        return metricData?.[trendType] || 0;
                    }),
                    color: colors[0]
                };
                chartSeries.push(groupData);
            } else {
                // Add current facility data - get from currentDeal.facilities
                const facilityData = {
                    name: facilityName,
                    data: chartPeriods.map(period => {
                        const periodData = currentDeal.facilities[facilityName]?.periods?.[period];
                        const categoryData = periodData?.Categories?.[type === 'EBITDAR' ? 'Expenses' : type] || [];
                        const metricData = categoryData.find(item => item.Type === selectedMetric);
                        return metricData?.[trendType] || 0;
                    }),
                    color: colors[0]
                };
                chartSeries.push(facilityData);
            }
        }

        // Add comparison facilities data
        if (selectedFacilities.length > 0) {
            const comparisonData = selectedFacilities
                .filter(facility => facility !== currentFacility?.details?.Facilities)
                .map((facility, index) => ({
                    name: facility,
                    data: chartPeriods.map(period => {
                        const periodData = currentDeal.facilities[facility]?.periods?.[period];
                        const categoryData = periodData?.Categories?.[type === 'EBITDAR' ? 'Expenses' : type] || [];
                        const metricData = categoryData.find(item => item.Type === selectedMetric);
                        return metricData?.[trendType] || 0;
                    }),
                    color: colors[(index + (chartSeries.length > 0 ? 1 : 0)) % colors.length]
                }));
            chartSeries = [...chartSeries, ...comparisonData];
        }

        return chartSeries;
    }, [selectedMetric, selectedFacilities, currentDeal, type, trendType, currentFacility, chartPeriods]);

    // Handle facility selection
    const handleFacilityChange = (e) => {
        const value = e.target.value;
        if (!value) return;

        setSelectedFacilities(prev => {
            if (prev.includes(value)) {
                return prev.filter(item => item !== value);
            }
            return [...prev, value];
        });
    };

    // Chart options
    const chartOptions = {
        chart: {
            type: 'line',
            height: '100%',
            toolbar: {
                show: true,
                tools: {
                    download: true,
                    selection: true,
                    zoom: false,
                    zoomin: true,
                    zoomout: true,
                    pan: false,
                    reset: true
                },
            },
            zoom: { enabled: true },
            parentHeightOffset: 0,
        },
        stroke: {
            curve: 'smooth',
            width: 2
        },
        xaxis: {
            categories: chartPeriods.map(formatPeriodLabel),
            labels: {
                rotate: -45,
                style: { fontSize: '12px' }
            }
        },
        yaxis: {
            labels: {
                formatter: (value) => {
                    return new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    }).format(value);
                }
            }
        },
        tooltip: {
            y: {
                formatter: (value) => {
                    return new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    }).format(value);
                }
            }
        },
        legend: {
            position: 'top',
            horizontalAlign: 'center',
            offsetY: 30,
            height: "auto",
        },
        grid: {
            padding: { top: 20 }
        },
        markers: {
            size: 4,
            hover: { size: 6 }
        }
    };

    return (
        <Card extra={"w-full h-full p-6"}>
            <div className="flex flex-col h-full">
                <div className="flex-none space-y-4">
                    {viewMode === 'portfolio' && (
                        <div className="inline-flex items-center bg-gray-100 rounded-lg p-1 shadow-sm w-fit">
                            <button
                                type="button"
                                onClick={() => setPeriodView('trailing')}
                                aria-pressed={periodView === 'trailing'}
                                className={`px-4 py-2 rounded-md transition-all duration-200 font-medium text-sm min-w-[100px] cursor-pointer ${
                                    periodView === 'trailing'
                                        ? 'bg-blue-500 text-white shadow-sm'
                                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                }`}
                            >
                                Trailing
                            </button>
                            <button
                                type="button"
                                onClick={() => setPeriodView('monthly')}
                                aria-pressed={periodView === 'monthly'}
                                className={`px-4 py-2 rounded-md transition-all duration-200 font-medium text-sm min-w-[100px] cursor-pointer ${
                                    periodView === 'monthly'
                                        ? 'bg-blue-500 text-white shadow-sm'
                                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                }`}
                            >
                                Monthly
                            </button>
                        </div>
                    )}
                    <RadioGroupField
                        name="trend-type"
                        orientation="horizontal"
                        value={trendType}
                        direction="row"
                        onChange={(e) => setTrendType(e.target.value)}
                        className="mb-2"
                    >
                        <Radio value="Annual">Annual</Radio>
                        <Radio value="PPD">PPD</Radio>
                    </RadioGroupField>
                    <div className='flex flex-col sm:flex-row gap-4'>
                        <SelectField
                            className="amplify-selectfield flex-grow"
                            variation="quiet"
                            value={selectedMetric}
                            onChange={(e) => setSelectedMetric(e.target.value)}
                        >
                            <option value="">Select a metric...</option>
                            {items?.map((item) => (
                                <option key={item} value={item}>{item}</option>
                            ))}
                        </SelectField>
                        <SelectField
                            placeholder="Facilities to Compare"
                            className="flex-grow"
                            variation="quiet"
                            value=""
                            onChange={handleFacilityChange}
                        >
                            {facilities?.map((facility) => (
                                <option key={facility} value={facility}>
                                    {facility} {selectedFacilities.includes(facility) ? '✓' : ''}
                                </option>
                            ))}
                        </SelectField>
                    </div>

                    {selectedFacilities.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {selectedFacilities?.map((facility) => (
                                <div key={facility} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded">
                                    <span>{facility}</span>
                                    <button
                                        onClick={() => setSelectedFacilities(prev => prev.filter(f => f !== facility))}
                                        className="ml-1 hover:text-blue-900"
                                    >×</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {selectedMetric && selectedFacilities.length > 0 ? (
                    <div className="flex-1 min-h-0 mt-4">
                        <ReactApexChart
                            options={{
                                ...chartOptions,
                                yaxis: {
                                    ...chartOptions.yaxis,
                                    title: {
                                        text: trendType === 'Annual' ? 'Annual Value ($)' : 'Per Patient Day ($)',
                                        style: {
                                            fontSize: '12px',
                                            fontWeight: 400,
                                            color: '#666'
                                        }
                                    }
                                }
                            }}
                            series={chartData}
                            type="line"
                            height="100%"
                        />
                    </div>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-500">
                        {!selectedMetric ? 'Select a metric to begin comparison' : 'Select facilities to compare'}
                    </div>
                )}
            </div>
        </Card>
    );
};

export default TrendChart;